import { fromJsonArray } from 'src/core/utils';
import CalculatedVolumeDiscount from 'src/entities/cart/response/CalculatedVolumeDiscount';
import OrderProduct from 'src/entities/cart/response/OrderProduct';
import VolumeDiscount from 'src/entities/volume-discount/VolumeDiscount';

export default class OrderProductListGrouped {
    localizedName: string;
    normalizedName: string;
    discount: number;
    totalPrice: number;
    products: OrderProduct[];
    currencyIso: string;

    constructor(json: any) {
        this.localizedName = json.localizedName;
        this.normalizedName = json.normalizedName;
        this.discount = json.discount;
        this.totalPrice = json.totalPrice;
        this.products = fromJsonArray(OrderProduct, json.products);
        this.currencyIso = this.products[0]?.currencyIso ?? '';
    }

    public get articleGroupNormalizedName(): string {
        return this.products[0] ? this.products[0].articleGroupNormalizedName : '';
    }

    public containsUnfinishedProductCustomFields(): boolean {
        return this.products.some(product => product.hasUnfinishedCustomFields());
    }

    /**
     * Calculates the total quantity of products within this group
     */
    public getTotalQuantity(targetUnit: string = ''): number {
        return this.products.reduce((total: number, product: OrderProduct) => {
            return getTotalQuantity(product.quantity, product.colliEntity, product.colli, targetUnit) + total;
        }, 0);
    }

    /**
     * Returns a calculated volume discount if this article group support it
     */
    public getCalculatedVolumeDiscount(volumeDiscounts: VolumeDiscount[]): CalculatedVolumeDiscount | undefined {
        // check if there is a potential volume discount available for this article group
        const matchingVolumeDiscount = volumeDiscounts.find(
            volumeDiscount => volumeDiscount.articleGroupNormalizedName === this.articleGroupNormalizedName
        );

        if (!matchingVolumeDiscount) return undefined;

        return new CalculatedVolumeDiscount(
            matchingVolumeDiscount,
            this.getTotalQuantity(matchingVolumeDiscount.baseColli),
            this.totalPrice,
            this.currencyIso
        );
    }
}

const UNIT_FACTORS = {
    g: { kg: 1000 },
    kg: { g: 0.001 },
};

export const getBaseUnitFactor = (unit: string) => getUnitFactor('g', unit);

export const getUnitFactor = (targetUnit: string, unit: string): number => {
    const target = UNIT_FACTORS[targetUnit] ?? {};
    return target[unit] ?? 1;
};

export const getTotalQuantity = (
    quantity: number,
    colliEntity: string,
    colli: number,
    targetUnit: string = ''
): number => {
    const uf = getUnitFactor(targetUnit, colliEntity);
    return quantity * colli * uf;
};
